import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  NotificationsOutlined,
  Close,
  DoneAll,
  CheckCircleOutline,
  RadioButtonUnchecked,
  AccountBalanceWalletOutlined,
  TaskAltOutlined,
  CancelOutlined,
  BookmarkBorderOutlined,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";

// ── Types ─────────────────────────────────────────────────────────────────────

interface NotificationApiItem {
  id: number;
  title: string;
  description: string;
  trigger_source: string;
  status: "read" | "unread" | "archived";
  object_id?: number;
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface NotificationItem {
  id: string;
  title: string;
  description: string;
  trigger_source: string;
  status: "read" | "unread" | "archived";
  record_id: number;
  timestamp: string;
  metadata: Record<string, unknown>;
}

interface PaginatedResponse {
  results: NotificationApiItem[];
  next: string | null;
  count: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

// ── Helpers ───────────────────────────────────────────────────────────────────

function mapApiItem(item: NotificationApiItem): NotificationItem {
  return {
    id: String(item.id),
    title: item.title || "Notification",
    description: item.description || "",
    trigger_source: item.trigger_source || "",
    status: item.status || "unread",
    record_id: item.object_id ?? 0,
    timestamp: item.created_at,
    metadata: item.metadata ?? {},
  };
}

function formatRelativeTime(iso: string) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

function getNotificationIcon(triggerSource: string, title: string) {
  const t = title.toLowerCase();
  const src = triggerSource.toLowerCase();

  if (src.includes("wallet")) {
    if (t.includes("complet"))
      return <TaskAltOutlined sx={{ fontSize: 20, color: "#1565c0" }} />;
    if (t.includes("approv"))
      return <CheckCircleOutline sx={{ fontSize: 20, color: "#4caf50" }} />;
    if (t.includes("reject") || t.includes("declin"))
      return <CancelOutlined sx={{ fontSize: 20, color: "#f44336" }} />;
    return (
      <AccountBalanceWalletOutlined sx={{ fontSize: 20, color: "#f59e0b" }} />
    );
  }
  if (src.includes("booking")) {
    if (t.includes("approv") || t.includes("confirm"))
      return <CheckCircleOutline sx={{ fontSize: 20, color: "#4caf50" }} />;
    if (t.includes("declin") || t.includes("cancel"))
      return <CancelOutlined sx={{ fontSize: 20, color: "#f44336" }} />;
    return <BookmarkBorderOutlined sx={{ fontSize: 20, color: "#8b5cf6" }} />;
  }
  return <NotificationsOutlined sx={{ fontSize: 20, color: "#888" }} />;
}

function getIconBg(triggerSource: string, title: string) {
  const t = title.toLowerCase();
  const src = triggerSource.toLowerCase();
  if (src.includes("wallet")) {
    if (t.includes("complet")) return "#e3f2fd";
    if (t.includes("approv")) return "#e8f5e9";
    if (t.includes("reject") || t.includes("declin")) return "#ffebee";
    return "#fff8e1";
  }
  if (src.includes("booking")) {
    if (t.includes("approv") || t.includes("confirm")) return "#e8f5e9";
    if (t.includes("declin") || t.includes("cancel")) return "#ffebee";
    return "#f3e8ff";
  }
  return "#f5f5f5";
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ReferralNotificationBell() {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const color800 = reduxTheme.primary_colors?.[800] ?? "#724E41";

  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"unread" | "read" | "all">(
    "unread",
  );
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // ── Fetch unread count for badge ──────────────────────────────────────────

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiService.get<{ unread_count: number }>(
        "/api/notifications/notification/unread-count/",
      );
      setUnreadCount(res.data.unread_count ?? 0);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // ── Build URL ─────────────────────────────────────────────────────────────

  const buildUrl = (filter: "unread" | "read" | "all") => {
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status", filter);
    return `/api/notifications/notification/?${params.toString()}`;
  };

  // ── Fetch notifications (partner sources only) ───────────────────────────

  const fetchNotifications = useCallback(
    async (filter: "unread" | "read" | "all") => {
      setLoading(true);
      setNotifications([]);
      setNextPageUrl(null);
      try {
        const res = await apiService.get<PaginatedResponse>(buildUrl(filter));
        setNotifications((res.data.results ?? []).map(mapApiItem));
        setNextPageUrl(res.data.next || null);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadMore = async () => {
    if (!nextPageUrl) return;
    setLoadingMore(true);
    try {
      const res = await apiService.get<PaginatedResponse>(nextPageUrl);
      const mapped = (res.data.results ?? []).map(mapApiItem);
      setNotifications((prev) => [...prev, ...mapped]);
      setNextPageUrl(res.data.next || null);
    } catch (err) {
      console.error("Failed to load more:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Mark read / unread ────────────────────────────────────────────────────

  const handleMarkRead = async (id: string) => {
    try {
      await apiService.patch(
        `/api/notifications/notification/${id}/mark-read/`,
        {},
      );
      setNotifications((prev) =>
        statusFilter === "unread"
          ? prev.filter((n) => n.id !== id)
          : prev.map((n) =>
              n.id === id ? { ...n, status: "read" as const } : n,
            ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const handleMarkUnread = async (id: string) => {
    try {
      await apiService.patch(
        `/api/notifications/notification/${id}/mark-unread/`,
        {},
      );
      setNotifications((prev) =>
        statusFilter === "read"
          ? prev.filter((n) => n.id !== id)
          : prev.map((n) =>
              n.id === id ? { ...n, status: "unread" as const } : n,
            ),
      );
      setUnreadCount((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to mark as unread:", err);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await apiService.post(
        "/api/notifications/notification/mark-all-read/",
        {},
      );
      await fetchNotifications(statusFilter);
      fetchUnreadCount();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    } finally {
      setMarkingAll(false);
    }
  };

  // ── Open / refetch on filter change ──────────────────────────────────────

  const handleOpen = () => {
    setStatusFilter("unread");
    setOpen(true);
    fetchUnreadCount();
  };

  useEffect(() => {
    if (open) fetchNotifications(statusFilter);
  }, [open, statusFilter, fetchNotifications]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton onClick={handleOpen} aria-label="Notifications">
          <Badge
            badgeContent={unreadCount || undefined}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "10px",
                height: 18,
                minWidth: 18,
              },
            }}
          >
            <NotificationsOutlined sx={{ fontSize: 24 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "90%", sm: 400 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" fontWeight="bold" fontSize={20}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {unreadCount} unread
                </Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title="Mark all as read" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleMarkAllRead}
                    disabled={markingAll}
                  >
                    {markingAll ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DoneAll fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton onClick={() => setOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          {/* Status filter chips */}
          <Box display="flex" gap={1} mt={1.5} mb={2}>
            {(["unread", "all", "read"] as const).map((f) => (
              <Chip
                key={f}
                label={f.charAt(0).toUpperCase() + f.slice(1)}
                size="small"
                clickable
                onClick={() => setStatusFilter(f)}
                sx={{
                  fontWeight: statusFilter === f ? 600 : 400,
                  bgcolor: statusFilter === f ? color400 : "transparent",
                  color: statusFilter === f ? "#fff" : "text.secondary",
                  border: `1px solid ${statusFilter === f ? color400 : "#e0e0e0"}`,
                  "&:hover": {
                    bgcolor: statusFilter === f ? color400 : "#f5f5f5",
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box textAlign="center" py={8} color="text.secondary">
              <NotificationsOutlined
                sx={{ fontSize: 44, opacity: 0.25, mb: 1 }}
              />
              <Typography variant="body2">No notifications found.</Typography>
            </Box>
          ) : (
            <AnimatePresence mode="popLayout">
              {notifications.map((n) => {
                const isUnread = n.status === "unread";
                return (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        mb: 1.5,
                        bgcolor: isUnread ? color800 : "action.hover",
                        borderLeft: isUnread
                          ? `2px solid ${color400}`
                          : "3px solid transparent",
                      }}
                    >
                      <Box display="flex" gap={1.5} alignItems="flex-start">
                        {/* Icon avatar */}
                        <Avatar
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: getIconBg(n.trigger_source, n.title),
                            flexShrink: 0,
                          }}
                        >
                          {getNotificationIcon(n.trigger_source, n.title)}
                        </Avatar>

                        {/* Content */}
                        <Box flex={1} minWidth={0}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={1}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={isUnread ? 700 : 400}
                              sx={{ lineHeight: 1.3, flex: 1 }}
                            >
                              {n.title}
                            </Typography>

                            {isUnread ? (
                              <Tooltip title="Mark as read">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkRead(n.id)}
                                  sx={{ flexShrink: 0, p: 0.3 }}
                                >
                                  <CheckCircleOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Mark as unread">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkUnread(n.id)}
                                  sx={{ flexShrink: 0, p: 0.3 }}
                                >
                                  <RadioButtonUnchecked fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>

                          {n.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{
                                mt: 0.5,
                                whiteSpace: "pre-line",
                                lineHeight: 1.4,
                              }}
                            >
                              {n.description}
                            </Typography>
                          )}

                          <Typography
                            variant="caption"
                            color="text.disabled"
                            display="block"
                            mt={0.5}
                          >
                            {formatRelativeTime(n.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}

          {nextPageUrl && !loading && (
            <Box textAlign="center" mt={2} mb={2}>
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: color400,
                  color: color400,
                  textTransform: "none",
                }}
              >
                {loadingMore ? <CircularProgress size={16} /> : "Load more"}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}
